// Formatting helpers and metadata for the Traffic Control panel.
// Every formatter returns "—" for null / undefined / NaN so the UI never shows "NaN" or "0" for "not computable".
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const isNil = (v) => v === null || v === undefined || v === "" || Number.isNaN(Number(v));

const intFormat = new Intl.NumberFormat("en-US", {maximumFractionDigits: 0});
const moneyFormat = new Intl.NumberFormat("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2});
const numFormats = {};

const numFormat = (digits) => {
    if (!numFormats[digits]) {
        numFormats[digits] = new Intl.NumberFormat("en-US", {minimumFractionDigits: digits, maximumFractionDigits: digits});
    }
    return numFormats[digits];
};

export const EMPTY = "—";
export const MINUS = "−"; // U+2212, same width as "+" in tabular figures

/** "$1,234.56" */
export const fmtMoney = (v) => (isNil(v) ? EMPTY : "$" + moneyFormat.format(Number(v)));

/** "1,234" */
export const fmtInt = (v) => (isNil(v) ? EMPTY : intFormat.format(Math.round(Number(v))));

/** Fraction → percent: 0.089 → "8.9%" */
export const fmtPct = (v, digits = 1) => (isNil(v) ? EMPTY : numFormat(digits).format(Number(v) * 100) + "%");

/** Fraction → percent, no decimals: 1.4626 → "146%" */
export const fmtPct0 = (v) => fmtPct(v, 0);

/** Plain number with fixed decimals: 3.2712 → "3.27" */
export const fmtNum = (v, digits = 2) => (isNil(v) ? EMPTY : numFormat(digits).format(Number(v)));

/** Coverage ratio rendered as percent: 1.151 → "115.1%" */
export const fmtRatio = (v, digits = 1) => fmtPct(v, digits);

/** Money without cents once it is ≥ $10: 2514.8 → "$2,515", 3.77 → "$3.77" */
export const fmtMoneyShort = (v) => {
    if (isNil(v)) return EMPTY;
    const n = Number(v);
    if (n === 0) return "$0";
    return Math.abs(n) >= 10 ? "$" + intFormat.format(Math.round(n)) : "$" + numFormat(2).format(n);
};

/** Money with 2 dp, dropping cents at ≥ $100 (RPM-like values): 3.2712 → "$3.27", 100.81 → "$101" */
export const fmtMoneyShort2 = (v) => {
    if (isNil(v)) return EMPTY;
    const n = Number(v);
    return Math.abs(n) >= 100 ? "$" + intFormat.format(Math.round(n)) : "$" + numFormat(2).format(n);
};

/** Compact volume: 362942 → "363k", 6946 → "6.9k", 9000355 → "9.0M", 412 → "412" */
export const fmtCompact = (v) => {
    if (isNil(v)) return EMPTY;
    const n = Number(v);
    const a = Math.abs(n);
    if (a >= 1e6) return numFormat(1).format(n / 1e6) + "M";
    if (a >= 1e4) return intFormat.format(Math.round(n / 1e3)) + "k";
    if (a >= 1e3) return numFormat(1).format(n / 1e3) + "k";
    return intFormat.format(Math.round(n));
};

/** Delta already expressed in percent points (16.0 → "+16.0%") */
export const fmtDelta = (v, digits = 1) => {
    if (isNil(v)) return EMPTY;
    const n = Number(v);
    const sign = n > 0 ? "+" : n < 0 ? MINUS : "";
    return sign + numFormat(digits).format(Math.abs(n)) + "%";
};

/**
 * Grid delta: "+76%" / "−17%" (0 dp; 1 dp when |Δ| < 10), "new" only for a genuinely new tag (revenue now, none in the
 * previous window), "—" whenever the current window earned nothing, ">10×" above 999 %.
 */
export const fmtDeltaShort = (v, prev, cur) => {
    const p = isNil(prev) ? 0 : Number(prev);
    const c = isNil(cur) ? 0 : Number(cur);
    if (c === 0) return EMPTY;
    if (p === 0) return "new";
    if (isNil(v)) return EMPTY;
    const n = Number(v);
    if (n > 999) return ">10×";
    const digits = Math.abs(n) < 10 ? 1 : 0;
    return fmtDelta(n, digits);
};

/** MUI palette path for a delta value: green up, red down, muted when small or absent. */
export const deltaColor = (v, prev, cur) => {
    const p = isNil(prev) ? 0 : Number(prev);
    const c = isNil(cur) ? 0 : Number(cur);
    if (p === 0 || c === 0) return "text.disabled";
    if (isNil(v) || Math.abs(Number(v)) < 10) return "rgba(0,0,0,.38)";
    return Number(v) > 0 ? "success.dark" : "error.main";
};

/** "2026-09-07" → "09-07" */
export const shortDay = (day) => (typeof day === "string" && day.length >= 10 ? day.slice(5, 10) : day || EMPTY);

/** "2026-09-07" → "Sep 7" */
export const fmtDay = (day) => (day ? dayjs.utc(day).format("MMM D") : EMPTY);

/** "2026-09-07" → "Mon" */
export const fmtDow = (day) => (day ? dayjs.utc(day).format("dd") : EMPTY);

/** ("2026-09-07","2026-09-13") → "Sep 7–13"; across months "Aug 31–Sep 6"; across years adds the years. */
export const fmtRange = (from, to) => {
    if (!from || !to) return EMPTY;
    const a = dayjs.utc(from);
    const b = dayjs.utc(to);
    if (a.year() !== b.year()) return `${a.format("MMM D, YYYY")}–${b.format("MMM D, YYYY")}`;
    if (a.month() !== b.month()) return `${a.format("MMM D")}–${b.format("MMM D")}`;
    if (a.isSame(b, "day")) return a.format("MMM D");
    return `${a.format("MMM D")}–${b.format("D")}`;
};

/** UTC "yesterday" = last closed day, as a dayjs.utc at 00:00. */
export const utcYesterday = () => dayjs.utc().subtract(1, "day").startOf("day");

/** Range presets: `key` is a day count or "mtd" / "lm"; `label` for toggles, `long` for menus and aria. */
export const PRESETS = [
    {key: 7, label: "7d", long: "Last 7 closed days"},
    {key: "mtd", label: "MTD", long: "Month to date"},
    {key: "lm", label: "Last month", long: "Last month"},
];
export const presetLabel = (key) => (PRESETS.find((p) => p.key === key) || {}).label || null;

/**
 * Closed-days window as YYYY-MM-DD strings. N → to = UTC yesterday, from = to − N + 1. "mtd" → the month of UTC
 * yesterday up to yesterday (on the 1st that is the whole month just closed). "lm" → the full month before that one.
 */
export const presetRange = (preset) => {
    const to = utcYesterday();
    const fmt = (a, b) => ({from: a.format("YYYY-MM-DD"), to: b.format("YYYY-MM-DD")});
    if (preset === "mtd") return fmt(to.startOf("month"), to);
    if (preset === "lm") {
        const m = to.startOf("month").subtract(1, "month");
        return fmt(m, m.endOf("month").startOf("day"));
    }
    return fmt(to.subtract(preset - 1, "day"), to);
};

/** Safe division: null when the denominator is 0 / missing. */
export const ratio = (num, den) => (isNil(num) || isNil(den) || Number(den) === 0 ? null : Number(num) / Number(den));

export const isNilValue = isNil;

// ---------------------------------------------------------------------------------------------------------------------
// Status → severity

export const SEVERITY = {
    DEAD: "error", NO_ADS: "error", STOPPED: "error",
    WATCH: "warning", FILTERED: "warning", PRE_KILL: "warning",
    PARTIAL: "info", NO_TIGER: "info", RECOVERED: "info",
    NO_REVENUE: "idle", NO_TRAFFIC: "idle", RETIRED: "idle",
    OK: "ok",
};

export const SEVERITY_RANK = {error: 0, warning: 1, info: 2, ok: 3, idle: 4};

export const severityOf = (status) => SEVERITY[status] || "ok";
export const severityRank = (status) => SEVERITY_RANK[severityOf(status)];

export const IDLE_STATUSES = ["NO_REVENUE", "NO_TRAFFIC", "RETIRED"];
export const ALARM_STATUSES = ["DEAD", "NO_ADS", "STOPPED", "PRE_KILL", "WATCH", "FILTERED"];
export const ERROR_STATUSES = ["DEAD", "NO_ADS", "STOPPED", "PRE_KILL"];
export const INFO_STATUSES = ["PARTIAL", "NO_TIGER", "RECOVERED"];

/** Status metadata (brief v2.2 §1): label, short label (tablet — the full label, no abbreviations), plain-English hint. API keys unchanged. */
export const STATUS_META = {
    OK: {label: "OK", short: "", hint: "Healthy"},
    DEAD: {label: "DEAD", short: "DEAD", hint: "Was earning. Traffic still sent, but $0 on the last day."},
    STOPPED: {label: "STOPPED", short: "STOPPED", hint: "Was earning. Nothing sent and $0 on the last day(s)."},
    NO_ADS: {label: "NO ADS", short: "NO ADS", hint: "Pages load without an ad key, or under 1% of searches click an ad."},
    WATCH: {label: "WATCH", short: "WATCH", hint: "Pays below this tag's usual $/1k or revenue."},
    FILTERED: {label: "FILTERED", short: "FILTERED", hint: "Partner counts far fewer searches than we send (coverage collapsed)."},
    PRE_KILL: {label: "PRE-KILL", short: "PRE-KILL", hint: "CSE: under 80% of our valid clicks reach Google two days in a row — the account is being filtered; drain it."},
    PARTIAL: {label: "NOT REPORTED", short: "NOT REPORTED", hint: "Yesterday not reported by the partner yet."},
    NO_TIGER: {label: "OFF-TIGER", short: "OFF-TIGER", hint: "Earns, but this traffic does not pass through Tiger."},
    RECOVERED: {label: "RECOVERED", short: "RECOVERED", hint: "Back to normal yesterday after an alarm."},
    NO_REVENUE: {label: "NEVER EARNED", short: "NEVER EARNED", hint: "Traffic sent, $0 in this and the previous range."},
    NO_TRAFFIC: {label: "NO TRAFFIC", short: "NO TRAFFIC", hint: "Nothing sent in the range."},
    RETIRED: {label: "RETIRED", short: "RETIRED", hint: "Stopped for 8+ days (or nothing sent in the range): out of the counts and the $ at risk."},
};

export const statusMeta = (status) => STATUS_META[status] || {label: status || EMPTY, short: status || "", hint: ""};

/** Lower-case wording for counts: "2 not paying (DEAD)", "1 watch", "15 stopped", "3 not fully reported". */
export const STATUS_WORD = {
    DEAD: "not paying (DEAD)", NO_ADS: "no ads", STOPPED: "stopped", PRE_KILL: "pre-kill", WATCH: "watch", FILTERED: "filtered",
    PARTIAL: "not reported", NO_TIGER: "earning off-Tiger", RECOVERED: "recovered", NO_REVENUE: "never earned", NO_TRAFFIC: "nothing sent",
    RETIRED: "retired", OK: "ok",
};

/** One-line legend under the attention chips (permanent). */
export const STATUS_LEGEND = "DEAD = traffic in, $0 out · STOPPED = nothing in, nothing out · NO ADS = pages without ads · PRE-KILL = Google filtering a CSE account · WATCH = pays below its usual · FILTERED = partner counts far fewer searches";

/** Metrics selectable in `days` mode, with their per-day formatter and how to total a day across tags. */
export const DAY_METRICS = [
    {key: "rev", label: "Revenue", fmt: fmtMoneyShort, total: (s) => s.rev},
    {key: "net", label: "Net revenue", fmt: fmtMoneyShort, total: (s) => s.net},
    {key: "rpm", label: "RPM", fmt: fmtMoneyShort2, total: (s) => (s.mon > 0 ? (s.rev / s.mon) * 1000 : null)},
    {key: "rpm_fs", label: "$/1k sent", fmt: fmtMoneyShort2, total: (s) => (s.fs > 0 ? (s.rev / s.fs) * 1000 : null)},
    {key: "coverage", label: "Coverage", fmt: fmtPct0, total: (s) => ratio(s.mon, s.fs)},
    {key: "fs", label: "Sent", fmt: fmtCompact, total: (s) => s.fs},
    {key: "ctr", label: "CTR", fmt: fmtPct, total: (s) => ratio(s.clicks, s.mon)},
];

// ---------------------------------------------------------------------------------------------------------------------
// Row helpers

/** Mean revenue of the days that earned something; null when none did. */
export const earningMean = (row) => {
    const days = (row.daily || []).filter((d) => Number(d.rev) > 0);
    if (!days.length) return null;
    return days.reduce((s, d) => s + Number(d.rev), 0) / days.length;
};

/** Money at risk today: how far the last day fell below the tag's usual earning day. */
export const usualOf = (row) => {
    const y = row && row.yesterday;
    if (y && !isNil(y.usual_rev)) return Number(y.usual_rev);
    if (y && !isNil(y.base_rev)) return Number(y.base_rev);
    return earningMean(row);
};

export const atRisk = (row) => {
    const y = row && row.yesterday;
    const base = usualOf(row);
    const last = y && !isNil(y.rev) ? Number(y.rev) : (Number(row.rev_last) || 0);
    if (base !== null && base > 0) return Math.max(0, base - last);
    const mean = earningMean(row);
    if (mean === null) return 0;
    return Math.max(0, mean - (Number(row.rev_last) || 0));
};

const ISO_DAY = /\b(\d{4})-(\d{2})-(\d{2})\b/g;

/**
 * The API's plain-English status reason, rendered as is (v2.2: no client-side rewriting). Only ISO dates, should any
 * remain, become "Sep 12". Fallback per status when the API sends none.
 */
export const reasonOf = (row) => {
    const reason = row && row.status_reason;
    if (reason) return String(reason).replace(ISO_DAY, (m) => fmtDay(m));
    if (!row) return EMPTY;
    if (row.status === "NO_TRAFFIC") return "nothing sent in the range";
    if (row.status === "OK") return "healthy";
    return statusMeta(row.status).hint;
};

const SINCE_DAY = /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{1,2}\b/;

/** Grouping key for the attention list: the "Sep 12"-style date in the reason, or "range" when it names none. */
export const sinceKey = (row) => {
    const m = SINCE_DAY.exec(reasonOf(row));
    return m ? m[0] : "range";
};

/**
 * Partner-name slugs that stand for a network even when the words differ (a typo in the partner name, or a sub-account of
 * the network): slug found in the partner name → network labels / prefixes it covers (all lower-case).
 */
const PROVIDER_SLUGS = {
    mediasacle: ["mediascale", "tt"],
    mediascale: ["mediascale", "tt"],
    shunymobi: ["shunymobi", "xu"],
    deckermedia: ["zl / taboola", "taboola", "jo"],
    timman: ["zl / taboola", "taboola", "jo"],
    unicorn: ["zl / taboola", "taboola", "jo"],
};

/**
 * True when the partner name already carries the network label (then the row shows only the partner): a word of the
 * network label appears in the partner name, or a known slug of the partner covers the network label / its prefix.
 */
export const providerCoversNetwork = (provider, network, prefix) => {
    if (!provider || !network) return true;
    const p = String(provider).toLowerCase();
    const n = String(network).toLowerCase();
    if (n.split(/[\s/]+/).filter((w) => w.length > 2).some((w) => p.includes(w))) return true;
    const px = prefix ? String(prefix).toLowerCase() : null;
    return Object.keys(PROVIDER_SLUGS).some((slug) => p.includes(slug) && PROVIDER_SLUGS[slug].some((c) => c === n || (px && c === px)));
};

/** "CSE 1 - TYP" → "CSE 1", "Taboola Shunymobi - Dmd" → "Taboola Shunymobi" */
export const shortProvider = (name) => (name ? String(name).replace(/\s+-\s+(TYP|Dmd|Demand)$/i, "").replace(/Mediasacle/i, "Mediascale") : EMPTY);

/** Prefix-based short group name for chips on phone: "ZL / Taboola" → "ZL / Taboola" (kept), long ones trimmed. */
export const shortGroup = (label) => (label && label.length > 18 ? label.slice(0, 17) + "…" : label || EMPTY);

// ---------------------------------------------------------------------------------------------------------------------
// Yesterday comparison (v2.1)

/** Money with a k / M suffix once it is ≥ $10k: 29734 → "$29.7k", 2347 → "$2,347", 1.2e6 → "$1.20M". */
export const fmtMoneyCompact = (v) => {
    if (isNil(v)) return EMPTY;
    const n = Number(v);
    const a = Math.abs(n);
    const sign = n < 0 ? MINUS : "";
    if (a >= 1e6) return sign + "$" + numFormat(2).format(a / 1e6) + "M";
    if (a >= 1e4) return sign + "$" + numFormat(1).format(a / 1e3) + "k";
    return sign + fmtMoneyShort(a);
};

/** Signed money, no cents from $10: 15.08 → "+$15", −0.4 → "−$0.4", 0.02 → "$0". */
export const fmtSignedMoney = (v) => {
    if (isNil(v)) return EMPTY;
    const n = Number(v);
    const a = Math.abs(n);
    if (a < 0.05) return "$0";
    const body = a >= 10 ? intFormat.format(Math.round(a)) : numFormat(1).format(a);
    return (n > 0 ? "+" : MINUS) + "$" + body;
};

/** Signed percent already in points: 48.31 → "+48%", −0.87 → "−0.9%", null → "—". */
export const fmtSignedPct = (v) => {
    if (isNil(v)) return EMPTY;
    const n = Number(v);
    if (n > 999) return intFormat.format(Math.round(1 + n / 100)) + "×";
    return fmtDelta(n, Math.abs(n) < 10 ? 1 : 0);
};

/** Compare baselines for every "Δd" on the page. `key` is what the API field names use. */
export const COMPARE_BASES = [
    {key: "base", label: "7d avg", field: "delta_base_pct", refLabel: "7d avg"},
    {key: "d1", label: "day before", field: "delta_d1_pct", refLabel: "day before"},
];
export const compareBaseMeta = (key) => COMPARE_BASES.find((b) => b.key === key) || COMPARE_BASES[0];
export const readCompareBase = () => {
    try {
        const v = window.localStorage.getItem("tc.compareBase");
        if (COMPARE_BASES.some((b) => b.key === v)) return v;
    } catch (e) { /* ignore */ }
    return "base";
};

/** The Δd value of a row's `yesterday` block for the chosen baseline (null when not computable). */
export const ydDelta = (y, base) => {
    if (!y) return null;
    const v = y[compareBaseMeta(base).field];
    return isNil(v) ? null : Number(v);
};

/** Reference value the Δd compares against: the 7-day mean or the day before. */
export const ydRef = (y, base) => (!y ? null : base === "d1" ? y.rev_d2 : y.base_rev);

/**
 * Δd text: "+48%" / "−15%", "◌ not yet" while the pull is pending, "new" only for a tag without history that earned
 * yesterday (a new tag at $0 shows "—"), "—" when nothing to compare.
 */
export const fmtYd = (y, base) => {
    if (!y) return EMPTY;
    if (y.state === "pending") return "◌ not yet";
    if (y.state === "new") return Number(y.rev) > 0 ? "new" : EMPTY;
    return fmtSignedPct(ydDelta(y, base));
};

/** True when the API flagged the revenue itself as outside the tag's usual range (`moved_by` includes "rev"). */
export const ydRevMoved = (y) => Boolean(y && Array.isArray(y.moved_by) && y.moved_by.includes("rev"));

/**
 * Δd colour: sign colour only when the move is outside the tag's usual range (`moved_by` includes "rev") AND |Δ| ≥ 10 %;
 * disabled for pending / new; grey otherwise.
 */
export const ydColor = (y, base) => {
    if (!y || y.state === "pending") return "text.disabled";
    if (y.state === "new") return "text.disabled";
    const v = ydDelta(y, base);
    if (ydRevMoved(y) && v !== null && Math.abs(v) >= 10) return v > 0 ? "success.dark" : "error.main";
    return "text.secondary";
};

const DECOMP_LABEL = {sent: "sent", coverage: "coverage", rpm: "RPM"};

/**
 * Decomposition parts sorted by |$| desc: [{key, label, value}] — empty when the API could not attribute, or when every
 * part rounds to $0 (a "Why" line made of zeros explains nothing). The API `decomp` explains rev − rev_d2, i.e. the move
 * vs the DAY BEFORE, never the 7-day delta: label it "Why vs day before".
 */
export const DECOMP_BASIS = "Why vs day before";
export const decompParts = (y) => {
    if (!y || !y.decomp) return [];
    const parts = ["sent", "coverage", "rpm"].filter((k) => !isNil(y.decomp[k]))
        .map((k) => ({key: k, label: DECOMP_LABEL[k], value: Number(y.decomp[k])}))
        .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    return parts.some((p) => Math.abs(p.value) >= 0.05) ? parts : [];
};

/** `usually $22/d` or, with fewer than 5 earning days behind it, `usually $22/d (3 d)` — a thin base must say so. */
export const usualLabel = (y, fallback = null) => {
    const ref = y && !isNil(y.usual_rev) ? Number(y.usual_rev) : fallback;
    if (isNil(ref)) return `usually ${EMPTY}`;
    const n = y && !isNil(y.usual_n) ? Number(y.usual_n) : null;
    return `usually ${fmtMoneyShort(ref)}/d${n !== null && n < 5 ? ` (${n} d)` : ""}`;
};

/** Line 1 of the Δd tooltip: "Sep 13 $70 vs Sep 12 $82 · −15%" / "Sep 13 $70 vs 7d avg $82 · −15%". */
export const ydHeadline = (y, base) => {
    if (!y) return EMPTY;
    const ref = ydRef(y, base);
    const refLabel = base === "d1" ? fmtDay(dayjs.utc(y.day).subtract(1, "day").format("YYYY-MM-DD")) : "7d avg";
    const delta = ydDelta(y, base);
    return `${fmtDay(y.day)} ${fmtMoneyShort(y.rev)} vs ${refLabel} ${isNil(ref) ? EMPTY : fmtMoneyShort(ref)}${delta === null ? "" : ` · ${fmtSignedPct(delta)}`}`;
};

const DRIVER_WORD = {rpm: "pays", coverage: "ad coverage", sent: "traffic", drift: "below usual"};

/**
 * Driver phrase of a "moved yesterday" row from `moved_by` and the percentages in the API reason:
 * rpm → "pays −64% per 1k", coverage → "ad coverage +15%", sent → "traffic +11%"; the largest |$| in `decomp` first.
 * Falls back to the reason tail when `moved_by` is empty.
 */
export const driverPhrase = (c) => {
    if (!c) return "";
    const tail = typeof c.reason === "string" && c.reason.includes(": ") ? c.reason.split(": ").slice(1).join(": ") : "";
    const parts = tail.split(",").map((s) => s.trim()).filter(Boolean);
    const by = Array.isArray(c.moved_by) && c.moved_by.length ? c.moved_by : [];
    const decomp = c.decomp || {};
    const keys = by.length ? by : Object.keys(decomp).filter((k) => !isNil(decomp[k]));
    if (!keys.length) return parts.join(", ");
    const ordered = [...keys].sort((a, b) => Math.abs(Number(decomp[b]) || 0) - Math.abs(Number(decomp[a]) || 0));
    const phrase = ordered.map((k) => parts.find((p) => p.toLowerCase().startsWith(DRIVER_WORD[k] || k)) || null).filter(Boolean);
    return phrase.length ? phrase.join(", ") : parts.join(", ");
};
